import { IdentifierTypeEnum } from "@repo/graphql/gql/graphql";
import * as yup from "yup";
import { IDENTIFIER_HANDLERS } from "../identifier/handlers";

// Optional: a record without a country has no document types to pick from,
// and the backend detects the type of a number sent without one.
const typeSchema = yup
  .mixed<IdentifierTypeEnum>()
  .oneOf(Object.values(IdentifierTypeEnum), "Tipo de identificación inválido")
  .optional();

type IdentifierValue = { value?: string; type?: IdentifierTypeEnum };

/**
 * Checks the number against the rules of its type, the same the backend
 * applies, and reports on `value` so the error shows under the input. A
 * blank number is left to the required check, and one without a type is
 * left to the backend, which detects the type.
 */
const testIdentifierFormat: yup.TestFunction<IdentifierValue | undefined> =
  function (identifier) {
    const value = identifier?.value?.trim();
    const type = identifier?.type;
    if (!value || !type || !(type in IDENTIFIER_HANDLERS)) return true;

    const handler = IDENTIFIER_HANDLERS[type];
    if (handler.validate(value)) return true;
    return this.createError({
      path: this.path ? `${this.path}.value` : "value",
      message: handler.invalidMessage,
    });
  };

/**
 * Schema of the `{ value, type }` the identifier input holds. The format of
 * each typed document is checked here as well as by the backend, whose field
 * error is also shown under the input.
 */
const identifierSchema = (requiredMessage: string) =>
  yup
    .object({
      value: yup.string().trim().required(requiredMessage),
      type: typeSchema,
    })
    .test("identifier-format", testIdentifierFormat);

/** For an identifier the form may leave blank. */
export const optionalIdentifierSchema = yup
  .object({
    value: yup.string().trim().default(""),
    type: typeSchema,
  })
  .test("identifier-format", testIdentifierFormat);

export default identifierSchema;
